import { useState } from "react";

import { TextTestID } from "@/constants/TestID";
import { DateTimePickerComponent } from "@/crossPlatform/DateTimePicker";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal/BottomSheetModal";
import { Button } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

interface DatePickerBottomSheetModalProps extends ViewWithTestID {
  date?: Date;
  isOpen: boolean;
  maxDate?: Date;
  mode: DateTimePickerMode;
  onChange: (date: Date) => void;
  onRequestClose: () => void;
}

const getDatePickerModalTitle = (mode: DateTimePickerMode) => {
  switch (mode) {
    case "date":
      return "Select date";
    case "datetime":
      return "Select date and time";
    case "time":
      return "Select time";
    default:
      return "Select date and time";
  }
};

const DatePickerBottomSheetModal = ({
  date,
  isOpen,
  maxDate,
  mode,
  onChange,
  onRequestClose,
  testID,
}: DatePickerBottomSheetModalProps) => {
  const baseTestID = joinTestIDs(
    testID,
    TextTestID.DatePicker,
    TextTestID.BottomSheet,
  );
  const [selectedDate, setSelectedDate] = useState(date ?? new Date());

  const handleConfirmDate = () => {
    onChange(selectedDate);
    onRequestClose();
  };

  return (
    <BottomSheetModal
      dismissAccessibilityLabel="Dismiss date and time picker"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      testID={baseTestID}
      title={getDatePickerModalTitle(mode)}
    >
      <VStack gap={16}>
        <DateTimePickerComponent
          display="spinner"
          maximumDate={maxDate}
          mode={mode}
          // eslint-disable-next-line max-params
          onChange={(_, datePickerDate) => {
            if (datePickerDate) {
              setSelectedDate(datePickerDate);
            }
          }}
          style={{
            height: 200,
          }}
          themeVariant="light"
          value={selectedDate}
        />
        <HStack alignItems="center" justifyContent="space-between" width="100%">
          <Button
            onPress={onRequestClose}
            testID={joinTestIDs(baseTestID, TextTestID.Cancel)}
            title="Cancel"
            variant="tertiary"
          />
          <Button
            onPress={handleConfirmDate}
            testID={joinTestIDs(baseTestID, TextTestID.Confirm)}
            title="Confirm"
          />
        </HStack>
      </VStack>
    </BottomSheetModal>
  );
};

export type { DatePickerBottomSheetModalProps };
export default DatePickerBottomSheetModal;
